import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Wheat } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { SandwichDrawer } from "@/components/SandwichDrawer";
import { MENU, CATEGORIES, BREADS } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");

export default function Menu() {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [active, setActive] = useState(null);

  const items = category === CATEGORIES[0] ? MENU : MENU.filter((m) => m.category === category);

  return (
    <main data-testid="menu-page">
      <section className="relative overflow-hidden">
        <span
          aria-hidden="true"
          className="outline-word-dark pointer-events-none absolute -left-6 top-4 select-none font-display text-[20vw] font-black leading-none"
        >
          MENU
        </span>
        <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-16 sm:px-8 lg:pt-24">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-rust"
          >
            Fresh &amp; Authentic · {MENU.length} Sandwiches
          </motion.p>
          <MaskedLines
            className="mt-6 font-display text-4xl font-black leading-[1.05] tracking-tight text-ink sm:text-6xl lg:text-7xl"
            lines={[{ text: "The cold cut" }, { text: "menu.", cls: "italic text-rust" }]}
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-smoke"
          >
            Seven signature builds plus a full Halal menu — every sandwich sliced to order at the counter. Tap any
            build for the full breakdown.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.8, ease: EASE }}
            data-testid="bread-pricing-strip"
            className="mt-10 flex flex-col gap-6 overflow-hidden rounded-2xl border border-sand bg-card shadow-sm sm:flex-row"
          >
            <img
              src="/shop/bread-cabinet.jpg"
              alt="Fresh focaccia, Turkish bread and ciabatta rolls in the Babylon Deli bread cabinet"
              className="h-44 w-full object-cover sm:h-auto sm:w-64"
            />
            <div className="flex flex-col justify-center gap-3 px-6 py-6 sm:py-4">
              <span className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-smoke">
                <Wheat className="h-4 w-4 text-rust" />
                Your choice of bread — fresh daily
              </span>
              <div className="flex flex-wrap gap-x-8 gap-y-2">
                {BREADS.map((b) => (
                  <span key={b.name} className="font-mono text-sm tracking-wide text-ink">
                    {b.name} <span className="font-semibold text-rust">${b.price}</span>
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div data-testid="menu-filters" className="flex flex-wrap gap-2.5 py-2">
            {CATEGORIES.map((c) => {
              const isActive = c === category;
              return (
                <button
                  key={c}
                  data-testid={`menu-filter-${slug(c)}`}
                  onClick={() => setCategory(c)}
                  className={`relative rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                    isActive ? "text-cream" : "border border-sand bg-card text-smoke hover:border-rust hover:text-rust"
                  }`}
                >
                  {isActive && (
                    <motion.span
                      layoutId="filter-pill"
                      transition={{ duration: 0.45, ease: EASE }}
                      className={`absolute inset-0 rounded-full ${c === "Halal Menu" ? "bg-moss" : "bg-espresso"}`}
                    />
                  )}
                  <span className="relative z-10">{c}</span>
                </button>
              );
            })}
          </div>
        </Reveal>

        <motion.div layout data-testid="menu-grid" className="grid gap-x-8 gap-y-14 py-16 sm:grid-cols-2">
          <AnimatePresence mode="popLayout">
            {items.map((item, i) => (
              <motion.article
                layout
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.55, delay: i * 0.05, ease: EASE }}
              >
                <button
                  data-testid={`sandwich-card-${item.id}`}
                  onClick={() => setActive(item)}
                  className="group block w-full text-left"
                >
                  <div className="relative overflow-hidden rounded-2xl border border-sand bg-paper shadow-sm">
                    <img
                      src={item.image}
                      alt={item.alt}
                      loading="lazy"
                      className="aspect-[3/1] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span
                      className={`absolute left-4 top-4 rounded-full px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${
                        item.category === "Halal Menu" ? "bg-moss text-cream" : "bg-cream/95 text-ink backdrop-blur"
                      }`}
                    >
                      {item.chip}
                    </span>
                    <span className="absolute bottom-4 right-4 grid h-11 w-11 translate-y-2 place-items-center rounded-full bg-rust text-cream opacity-0 shadow-lg transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                      <ArrowUpRight className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="mt-5 flex items-baseline justify-between gap-4">
                    <h3 className="font-display text-xl font-bold tracking-tight text-ink transition-colors duration-300 group-hover:text-rust sm:text-2xl">
                      {item.name}
                    </h3>
                    <span className="shrink-0 font-mono text-sm font-semibold text-rust">from $16</span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-smoke">{item.description}</p>
                  {item.tags.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {item.tags.map((t) => (
                        <span
                          key={t}
                          className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] ${
                            t === "Halal" ? "bg-moss/10 text-moss" : "bg-paper text-smoke"
                          }`}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section className="double-rule bg-espresso text-cream">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 py-16 sm:px-8 md:flex-row md:items-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber">Hungry yet?</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">
              The slicer is warm. <span className="italic text-amber">Come by.</span>
            </h2>
          </div>
          <Link
            to="/#find-us"
            data-testid="menu-visit-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-amber px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-espresso transition-colors duration-300 hover:bg-rust hover:text-cream"
          >
            Plan Your Visit
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <SandwichDrawer item={active} onClose={() => setActive(null)} />
    </main>
  );
}
